import React, { useMemo } from 'react';
import { SypacText } from '@sypac/component-library-react';
import { T } from '@tolgee/react';
import dayjs from 'dayjs';
import { NumericFormat } from 'react-number-format';
import ProducerCompanyIcon from '../../../../assets/ProducerCompanyIcon';
import DashedDivider from '../../../../components/DashedDivider/DashedDivider';
import { cn } from '../../../../lib/utils';
import { Person } from '../../../../assets/Person';
import { SectionProps } from '../addNewContract.interface';
import Flag from 'react-world-flags';
import { numberTwoDecimal } from '../../../../utils/number.util';

const ContractSummary: React.FC<SectionProps> = ({ formik }) => {
	const buyerTotalVATExcl = useMemo(() => {
		const qty = Number(formik.values.quantity || 0);
		const price = Number(formik.values.pricePerTonBuyer || 0);
		return qty * price;
	}, [formik.values.quantity, formik.values.pricePerTonBuyer]);

	const sellerTotalVATExcl = useMemo(() => {
		const qty = Number(formik.values.quantity || 0);
		const price = Number(formik.values.pricePerTonSeller || 0);
		return qty * price;
	}, [formik.values.quantity, formik.values.pricePerTonSeller]);

	const toNum = (v: any) => Number(String(v ?? '').replace(',', '.')) || 0;
	const qtyNum = toNum(formik.values.quantity);

	const overdraftPercentRaw = String(
		formik.values.allowedOverdraftPercent ?? '',
	).trim();

	const overdraftPercent =
		overdraftPercentRaw !== '' ? toNum(overdraftPercentRaw) : NaN;

	const overdraftedQty =
		qtyNum > 0 && Number.isFinite(overdraftPercent)
			? qtyNum * (1 + overdraftPercent / 100)
			: null;

	const profit = useMemo(() => {
		const qty = toNum(formik.values.quantity);
		const perTonBuyer = toNum(formik.values.pricePerTonBuyer);
		const perTonSeller = toNum(formik.values.pricePerTonSeller);
		return (perTonBuyer - perTonSeller) * qty;
	}, [
		formik.values.quantity,
		formik.values.pricePerTonBuyer,
		formik.values.pricePerTonSeller,
	]);

	const isLoss = profit < 0;
	const absoluteProfit = Math.abs(profit);

	return (
		<div className="max-w-[440px] w-full h-fit flex flex-col items-stretch border border-solid border-gray-10 rounded-[12px] box-border shadow-[0_30px_40px_-40px_#00000080]">
			<div className="flex flex-col gap-4 pt-5 pb-4 box-border max-w-full">
				<SypacText variant="body-regular-medium">
					<p className="text-gray-80 text-[16px] leading-[24px] px-4">
						<T keyName="adminContracts.contractSummary">Contract summary</T>
					</p>
				</SypacText>

				{formik.values.buyerCompany || formik.values.sellerCompany ? (
					<>
						{formik.values.buyerCompany ? (
							<div className="h-fit min-h-[16px] px-4">
								<div className="flex justify-between items-start">
									<div className="h-[16px] flex items-center gap-3">
										<Person color="#A2A2A2" />
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
												<T keyName="adminContracts.buyerDots">Buyer:</T>
											</p>
										</SypacText>
									</div>

									<DashedDivider
										dashLength={2}
										gapLength={4}
										color="#ABABAB"
										className="self-start mx-[10px] mt-4"
									/>

									<div className="flex gap-2 items-center">
										{formik.values.buyerCompanyLogoUrl ? (
											<img
												src={formik.values.buyerCompanyLogoUrl}
												alt="Buyer company logo"
												className="w-[18px] h-[18px] flex-shrink-0 object-cover border border-solid border-gray-10 rounded-full overflow-hidden"
											/>
										) : (
											<div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center object-cover border border-solid border-gray-10 bg-white rounded-full">
												<Person width={10} />
											</div>
										)}
										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px] whitespace-nowrap">
												{formik.values.buyerLabel}
											</p>
										</SypacText>
									</div>
								</div>

								<div className="flex flex-col items-end gap-4 mt-4">
									<div className="flex items-start gap-1">
										{formik.values.buyerCompanyCountryCode ? (
											<Flag
												className="object-cover border border-solid border-gray-10 rounded"
												code={formik.values.buyerCompanyCountryCode}
												width={19}
												height={14}
											/>
										) : null}
										<SypacText variant="body-regular-medium">
											<p className="text-[14px] leading-[16px] text-gray-40 whitespace-normal break-words">
												{formik.values.buyerCompanyAddress}
											</p>
										</SypacText>
									</div>

									<div className="flex gap-2 items-center">
										{formik.values.buyerRepresentativePhotoUrl ? (
											<img
												src={formik.values.buyerRepresentativePhotoUrl}
												alt="Buyer representative avatar"
												className="w-[18px] h-[18px] object-cover border border-solid border-gray-10 rounded-full overflow-hidden"
											/>
										) : (
											<div className="w-[18px] h-[18px] flex items-center justify-center object-cover border border-solid border-gray-10 bg-white rounded-full">
												<Person width={10} />
											</div>
										)}
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												{formik.values.buyerRepresentativeLabel}
											</p>
										</SypacText>
									</div>
								</div>
							</div>
						) : null}

						{formik.values.sellerCompany ? (
							<div className="px-4 pt-4 border-0 border-t border-solid border-gray-10">
								<div className="h-fit min-h-[16px]">
									<div className="flex justify-between items-start">
										<div className="h-[16px] flex items-center gap-3">
											<ProducerCompanyIcon color="#A2A2A2" />
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] mt-[2px]">
													<T keyName="adminContracts.sellerDots">Seller:</T>
												</p>
											</SypacText>
										</div>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-start mx-[10px] mt-4"
										/>

										<div className="flex gap-2 items-center">
											{formik.values.sellerCompanyLogoUrl ? (
												<img
													src={formik.values.sellerCompanyLogoUrl}
													alt="Seller company logo"
													className="w-[18px] h-[18px] flex-shrink-0 object-cover border border-solid border-gray-10 rounded-full overflow-hidden"
												/>
											) : (
												<div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center object-cover border border-solid border-gray-10 bg-white rounded-full">
													<Person width={10} />
												</div>
											)}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px] whitespace-nowrap">
													{formik.values.sellerLabel}
												</p>
											</SypacText>
										</div>
									</div>

									<div className="flex flex-col items-end gap-4 mt-4">
										{formik.values.store ? (
											<div className="flex items-start gap-1">
												{formik.values.storeCountryCode ? (
													<span>
														<Flag
															className="object-cover border border-solid border-gray-10 rounded"
															code={formik.values.storeCountryCode}
															width={19}
															height={14}
														/>
													</span>
												) : null}
												<SypacText variant="body-regular-medium">
													<p className="w-fit text-end text-gray-40 text-[14px] leading-[16px] break-words">
														{formik.values.storeAddress}
													</p>
												</SypacText>
											</div>
										) : null}

										<div className="flex gap-2 items-center">
											{formik.values.sellerRepresentativePhotoUrl ? (
												<img
													src={formik.values.sellerRepresentativePhotoUrl}
													alt="Seller representative avatar"
													className="w-[18px] h-[18px] object-cover border border-solid border-gray-10 rounded-full overflow-hidden"
												/>
											) : (
												<div className="w-[18px] h-[18px] flex items-center justify-center object-cover border border-solid border-gray-10 bg-white rounded-full">
													<Person width={10} />
												</div>
											)}
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
													{formik.values.sellerRepresentativeLabel}
												</p>
											</SypacText>
										</div>
									</div>
								</div>
							</div>
						) : null}
					</>
				) : (
					<>
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 text-[20px] leading-[32px] px-4">
								<T keyName="adminContracts.selectContractParties">
									Select contract parties and duration
								</T>
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium">
							<p className="text-gray-40 text-[14px] leading-[16px] px-4">
								<T keyName="adminContracts.selectContractPartiesDesc">
									Choose the companies involved and set the contract timeline.
								</T>
							</p>
						</SypacText>
					</>
				)}
			</div>

			<div className="flex flex-col gap-4 pb-5 box-border max-w-full border-0 border-t border-solid border-gray-10">
				{formik.values.productName ||
				formik.values.pricePerTonBuyer ||
				formik.values.dateFrom ||
				formik.values.dateTo ||
				formik.values.quantity ? (
					<>
						{formik.values.productName || formik.values.quantity ? (
							<>
								{formik.values.productName ? (
									<>
										<div className="h-[16px] flex justify-between items-center pt-4 px-4">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
													<T keyName="adminContracts.productName">
														Product name:
													</T>
												</p>
											</SypacText>

											<DashedDivider
												dashLength={2}
												gapLength={4}
												color="#ABABAB"
												className="self-end mx-[10px]"
											/>

											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
													{formik.values.productLabel}
												</p>
											</SypacText>
										</div>

										<div className="h-[16px] flex justify-between items-center px-4">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
													<T keyName="adminContracts.productType">
														Product type:
													</T>
												</p>
											</SypacText>

											<DashedDivider
												dashLength={2}
												gapLength={4}
												color="#ABABAB"
												className="self-end mx-[10px]"
											/>

											<SypacText variant="body-regular-medium">
												<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
													{`${formik.values.productType || ''} ${
														formik.values.productSize
															? `(${formik.values.productSize})`
															: ''
													}`}
												</p>
											</SypacText>
										</div>
									</>
								) : null}

								{formik.values.quantity ? (
									<div
										className={cn(
											'h-[16px] flex justify-between items-center px-4',
											!formik.values.productName && 'pt-4',
										)}
									>
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.contractQuantity">
													Contract quantity:
												</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												{formik.values.quantity}{' '}
												<T keyName="basicDetails.tons">tons</T>
											</p>
										</SypacText>
									</div>
								) : null}

								{overdraftedQty !== null ? (
									<div className="h-[16px] flex justify-between items-center px-4">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.overdraftedQuantity">
													Overdrafted quantity:
												</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													decimalScale={2}
													fixedDecimalScale={false}
													value={overdraftedQty}
												/>{' '}
												<T keyName="basicDetails.tons">tons</T>
											</p>
										</SypacText>
									</div>
								) : null}
							</>
						) : null}

						{formik.values.pricePerTonBuyer ||
						formik.values.pricePerTonSeller ? (
							<div className="flex flex-col gap-4 p-4 pb-0 border-0 border-t border-solid border-gray-10">
								{formik.values.pricePerTonSeller ? (
									<div className="h-[16px] flex justify-between items-center">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.sellerPrice">
													Seller price:
												</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={formik.values.pricePerTonSeller || 0}
													suffix={` ${
														formik.values.countryCode === 'MD' ? 'MDL' : 'PLN'
													} / t`}
												/>
											</p>
										</SypacText>
									</div>
								) : null}
								{formik.values.pricePerTonBuyer ? (
									<div className="h-[16px] flex justify-between items-center">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.buyerPrice">Buyer price:</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={formik.values.pricePerTonBuyer || 0}
													suffix={` ${
														formik.values.countryCode === 'MD' ? 'MDL' : 'PLN'
													} / t`}
												/>
											</p>
										</SypacText>
									</div>
								) : null}
								{formik.values.markup ? (
									<div className="h-[16px] flex justify-between items-center">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.markUp">SYPAC Mark-up:</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={formik.values.markup || 0}
													suffix=" %"
												/>
											</p>
										</SypacText>
									</div>
								) : null}

								{formik.values.allowedOverdraftPercent ? (
									<div className="h-[16px] flex justify-between items-center">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T keyName="adminContracts.overdraft">Overdraft:</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={formik.values.allowedOverdraftPercent}
													suffix=" %"
												/>
											</p>
										</SypacText>
									</div>
								) : null}

								{formik.values.quantity &&
								formik.values.pricePerTonBuyer &&
								formik.values.pricePerTonSeller ? (
									<div className="h-[16px] flex justify-between items-center">
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
												<T
													keyName={
														isLoss
															? 'adminContracts.sypacLoss'
															: 'adminContracts.sypacProfit'
													}
												>
													{isLoss ? 'SYPAC Loss:' : 'SYPAC Profit:'}
												</T>
											</p>
										</SypacText>

										<DashedDivider
											dashLength={2}
											gapLength={4}
											color="#ABABAB"
											className="self-end mx-[10px]"
										/>

										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] whitespace-nowrap">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={numberTwoDecimal(absoluteProfit)}
													prefix={isLoss ? '-' : ''}
													suffix={
														formik.values.countryCode === 'MD' ? ' MDL' : ' PLN'
													}
													allowNegative={false}
												/>
											</p>
										</SypacText>
									</div>
								) : null}
							</div>
						) : null}
					</>
				) : (
					<>
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 text-[20px] leading-[32px] px-4 pt-4">
								<T keyName="adminContracts.selectProductQuantity">
									Select product and quantity
								</T>
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium">
							<p className="text-gray-40 text-[14px] leading-[16px] px-4">
								<T keyName="adminContracts.selectProductQuantityDesc">
									Select a product, set quantity, and add pricing to calculate
									totals.
								</T>
							</p>
						</SypacText>
					</>
				)}

				{formik.values.dateFrom || formik.values.dateTo ? (
					<div className="flex flex-col gap-4 p-4 pb-0 border-0 border-t border-solid border-gray-10">
						{formik.values.dateFrom ? (
							<div className="h-[16px] flex justify-between items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
										<T keyName="adminContracts.contractValidityFrom">
											Contract validity from:
										</T>
									</p>
								</SypacText>

								<DashedDivider
									dashLength={2}
									gapLength={4}
									color="#ABABAB"
									className="self-end mx-[10px]"
								/>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(formik.values.dateFrom).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						) : null}

						{formik.values.dateTo ? (
							<div className="h-[16px] flex justify-between items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px] whitespace-nowrap">
										<T keyName="adminContracts.contractValidityTo">
											Contract validity to:
										</T>
									</p>
								</SypacText>

								<DashedDivider
									dashLength={2}
									gapLength={4}
									color="#ABABAB"
									className="self-end mx-[10px]"
								/>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
										{dayjs(formik.values.dateTo).format('DD.MM.YYYY')}
									</p>
								</SypacText>
							</div>
						) : null}
					</div>
				) : null}

				{formik.values.quantity &&
				(formik.values.pricePerTonBuyer || formik.values.pricePerTonSeller) ? (
					<div className="flex flex-col gap-4 pt-4 px-4 border-0 border-t border-solid border-gray-10">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 text-[16px] leading-[24px]">
								<T keyName="adminContracts.total">Total</T>
							</p>
						</SypacText>

						{formik.values.pricePerTonBuyer ? (
							<div className="h-[24px] flex justify-between items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[16px] leading-[24px] whitespace-nowrap">
										<T keyName="adminContracts.buyerTotalVatExcl">
											Buyer Total price (VAT excl.)
										</T>
									</p>
								</SypacText>

								<DashedDivider
									dashLength={2}
									gapLength={4}
									color="#ABABAB"
									className="self-end mx-[10px]"
								/>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[16px] leading-[24px] whitespace-nowrap">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={numberTwoDecimal(buyerTotalVATExcl)}
											suffix={
												formik.values.countryCode === 'MD' ? ' MDL' : ' PLN'
											}
										/>
									</p>
								</SypacText>
							</div>
						) : null}

						{formik.values.pricePerTonSeller ? (
							<div className="h-[24px] flex justify-between items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[16px] leading-[24px] whitespace-nowrap">
										<T keyName="adminContracts.sellerTotalVatExcl">
											Seller Total price (VAT excl.)
										</T>
									</p>
								</SypacText>

								<DashedDivider
									dashLength={2}
									gapLength={4}
									color="#ABABAB"
									className="self-end mx-[10px]"
								/>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[16px] leading-[24px] whitespace-nowrap">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={numberTwoDecimal(sellerTotalVATExcl)}
											suffix={
												formik.values.countryCode === 'MD' ? ' MDL' : ' PLN'
											}
										/>
									</p>
								</SypacText>
							</div>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ContractSummary;
